import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { setBreadCrumb } from "middleware/actions/breadCrumbAction";
import { DateRangePickerComponent } from "@syncfusion/ej2-react-calendars";
import {
  downloadAutomationExecutionDetailedFileUrl,
  downloadAutomationRuleChangeFileUrl,
  exportAutomationRuleChangeReportData,
  exportExecutionDetailedReportData,
  fetchAutomationExecutionDetailedReport,
  fetchAutomationRuleChangeReport,
} from "middleware/services/reportApi";
import { enableRipple } from "@syncfusion/ej2-base";
import { hideLoader, showLoader } from "middleware/actions/utilityAction";
import { NumberFilter, StringFilter } from "app/components/dataGrid/fliters";
import NewPagination from "app/components/newPagination/newPagination";
import * as CONSTANTS from "../../../constants/index";
import { presets } from "utils";
import CustomSidebar from "./SideBar";
import CloseIcon from "../../../assets/images/closeIcon.svg?react";
import CommonDataGrid from "app/components/dataGrid";
import "./style.scss";
import { Tooltip } from "@mui/material";

// specify breadcrumb here
const breadCrumbLinks = [{ linkUrl: "/report/internal-user", linkName: "Internal User Report", linkActive: true }];
enableRipple(true);

const AutomationRuleChangeReport = (props) => {
  const dispatch = useDispatch();
  const sidebarRef = useRef(null);
  const [dateRange, setDateRange] = useState(null);
  const [startDate, setStartDate] = useState(moment(new Date()).format("dd-MMM-yyyy"));
  const [endDate, setEndDate] = useState(moment(new Date()).format("dd-MMM-yyyy"));
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState("");
  const [rowData, setRowData] = useState([]);
  const [filterValue, setFilterValue] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const showLoaderGrid = () => dispatch(showLoader());
  const hideLoaderGrid = () => dispatch(hideLoader());
  const gridState = useSelector((state) => state.gridState);

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    // Get UTC components of the date
    const day = String(date.getUTCDate()).padStart(2, "0");
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getUTCMonth()];
    const year = date.getUTCFullYear();

    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");

    // Construct the formatted date string
    const formattedDate = `${day}-${month}-${year} ${hours}:${minutes}`;

    return formattedDate;
  };

  const DateTemplate = (value) => {
    const column = value.column.field;
    return !isNaN(Date.parse(value[`${column}`])) ? <span>{formatDate(value[`${column}`])}</span> : "";
  };

  const handleRuleClick = (id) => {
    setShowSidePanel(true);
    setSelectedRuleId(id);
  };

  const numberOfReportsTemplate = (value) => {
    const column = value.column.field;
    return (
      <span className="number-of-reports cursor-pointer" onClick={() => handleRuleClick(value.id)}>
        {value[`${column}`]}
      </span>
    );
  };

  const automationCol = {
    id: "id",
    "Rule Name": "ruleName",
    "Last Executed Time": "lastExecutionTime",
    "Number Of Reports Updated": "numbersOfReportsUpdated",
  };

  const columnFields = [
    { field: "id", type: "string", visible: false, allowFiltering: false, showInColumnChooser: false, isPrimaryKey: true },
    {
      field: "Rule Name",
      type: "String",
      textAlign: "Left",
      width: 25,
      filterTemplate: StringFilter,
    },
    {
      field: "Last Executed Time",
      type: "Date",
      textAlign: "Center",
      width: 25,
      format: { type: "date", format: "dd-MMM-yyyy" },
      template: DateTemplate,
    },
    {
      field: "Number Of Reports Updated",
      type: "Number",
      showInColumnChooser: false,
      textAlign: "Left",
      width: 25,
      filterTemplate: NumberFilter,
      template: numberOfReportsTemplate,
    },
  ];

  const executionCol = {
    id: "id",
    "Execution ID": "executionId",
    "Previous Delivery Type": "previousDeliveryType",
    "New Delivery Type": "newDeliveryType",
    "Rule Name": "ruleName",
    "Execution Time Stamp": "executionTimestamp",
    "Content Title": "contentTitle",
    "Content Type": "contentType",
    "Sub Content Type": "subContentType",
    Industry: "industry",
    SGF: "sgf",
    Trend: "trend",
    "Update Status": "updateStatus",
    "Failure Reason": "failureReason",
    "Updated By": "updatedBy",
  };

  const truncateStyle = {
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden",
    fontSize: "14px",
  };

  const renderToolTip = (value, id) => {
    if (value.length >= 15) {
      return (
        <Tooltip title={value} arrow>
          <div id={id} style={truncateStyle}>
            {value.slice(0, 15) + "..."}
          </div>
        </Tooltip>
      );
    } else {
      return (
        <div id={id} style={truncateStyle}>
          {value}
        </div>
      );
    }
  };

  const RuleNameTemplate = (value) => {
    return <div>{!value["Rule Name"] ? "" : renderToolTip(value["Rule Name"], "ruleName")}</div>;
  };
  const ContentTitleTemplate = (value) => {
    return <div>{!value["Content Title"] ? "" : renderToolTip(value["Content Title"], "contentTitle")}</div>;
  };

  const ContentTypeTemplate = (value) => {
    return <div>{!value["Content Type"] ? "" : renderToolTip(value["Content Type"], "contentType")}</div>;
  };

  const SubContentTypeTemplate = (value) => {
    return <div>{!value["Sub Content Type"] ? "" : renderToolTip(value["Sub Content Type"], "subContentType")}</div>;
  };

  const IndustryTemplate = (value) => {
    return <div>{!value["Industry"] ? "" : renderToolTip(value["Industry"], "industry")}</div>;
  };

  const SgfTemplate = (value) => {
    return <div>{!value["SGF"] ? "" : renderToolTip(value["SGF"], "sgf")}</div>;
  };

  const TrendTemplate = (value) => {
    return <div>{!value["Trend"] ? "" : renderToolTip(value["Trend"], "trend")}</div>;
  };

  const FailureReasonTemplate = (value) => {
    return <div>{!value["Failure Reason"] ? "-" : renderToolTip(value["Failure Reason"], "failureReason")}</div>;
  };

  const UpdatedByTemplate = (value) => {
    return <div>{!value["Updated By"] ? "" : renderToolTip(value["Updated By"], "updatedBy")}</div>;
  };

  const modalColumnFields = [
    { field: "id", type: "string", visible: false, allowFiltering: false, showInColumnChooser: false, isPrimaryKey: true },
    {
      field: "Execution ID",
      type: "String",
      textAlign: "Left",
      width: 150,
      filterTemplate: StringFilter,
    },
    {
      field: "Previous Delivery Type",
      type: "String",
      textAlign: "Left",
      width: 220,
      filterTemplate: StringFilter,
    },
    {
      field: "New Delivery Type",
      type: "String",
      textAlign: "Left",
      width: 220,
      filterTemplate: StringFilter,
    },
    {
      field: "Rule Name",
      type: "String",
      textAlign: "Left",
      width: 150,
      filterTemplate: StringFilter,
      template: RuleNameTemplate,
    },
    {
      field: "Execution Time Stamp",
      type: "Date",
      textAlign: "Center",
      width: 220,
      format: { type: "date", format: "dd-MMM-yyyy" },
      template: DateTemplate,
    },
    {
      field: "Content Title",
      type: "String",
      textAlign: "Left",
      width: 150,
      filterTemplate: StringFilter,
      template: ContentTitleTemplate,
    },
    {
      field: "Content Type",
      type: "String",
      textAlign: "Left",
      width: 150,
      filterTemplate: StringFilter,
      template: ContentTypeTemplate,
    },
    {
      field: "Sub Content Type",
      type: "String",
      textAlign: "Left",
      width: 220,
      filterTemplate: StringFilter,
      template: SubContentTypeTemplate,
    },
    {
      field: "Industry",
      type: "String",
      textAlign: "Left",
      width: 150,
      filterTemplate: StringFilter,
      template: IndustryTemplate,
    },
    {
      field: "SGF",
      type: "String",
      textAlign: "Left",
      width: 150,
      filterTemplate: StringFilter,
      template: SgfTemplate,
    },
    {
      field: "Trend",
      type: "String",
      textAlign: "Left",
      width: 150,
      filterTemplate: StringFilter,
      template: TrendTemplate,
    },
    {
      field: "Update Status",
      type: "String",
      textAlign: "Left",
      width: 150,
      filterTemplate: StringFilter,
    },
    {
      field: "Failure Reason",
      type: "String",
      textAlign: "Left",
      width: 150,
      filterTemplate: StringFilter,
      template: FailureReasonTemplate,
    },
    {
      field: "Updated By",
      type: "String",
      textAlign: "Left",
      width: 150,
      filterTemplate: StringFilter,
      template: UpdatedByTemplate,
    },
  ];

  const formatRowData = (rowData) => {
    let formatedRowData = [];
    rowData.forEach((row) => {
      let data = {};
      data["id"] = row.id;
      data["Rule Name"] = row.ruleName;
      data["Last Executed Time"] = row.lastExecutionTime;
      data["Number Of Reports Updated"] = row.numbersOfReportsUpdated;
      formatedRowData.push(data);
    });
    return formatedRowData;
  };

  const formatExecutionRowData = (rowData) => {
    let formatedRowData = [];
    rowData.forEach((row) => {
      let data = {};
      data["id"] = row.id;
      data["Execution ID"] = row.executionId;
      data["Previous Delivery Type"] = row.previousDeliveryType;
      data["New Delivery Type"] = row.newDeliveryType;
      data["Rule Name"] = row.ruleName;
      data["Execution Time Stamp"] = row.executionTimestamp;
      data["Content Title"] = row.contentTitle;
      data["Content Type"] = row.contentType;
      data["Sub Content Type"] = row.subContentType;
      data["Industry"] = row.industry;
      data["SGF"] = row.sgf;
      data["Trend"] = row.trend;
      data["Update Status"] = row.updateStatus;
      data["Failure Reason"] = row.failureReason;
      data["Updated By"] = row.updatedBy;
      formatedRowData.push(data);
    });
    return formatedRowData;
  };

  const formik = useFormik({
    initialValues: {
      category: "",
      subCategory: "",
    },
  });

  const handelSideBar = () => {
    if (sidebarRef?.current) {
      sidebarRef.current.hide();
    }
    setShowSidePanel(!showSidePanel);
  };

  //** Fetch ask your analyst report
  const fetchAnalystQueryReport = async (reportDateRange = null, params = {}, state = false) => {
    try {
      dispatch(showLoader());
      clearLocalGrid();
      params.filters = [];
      params.sort = "lastExecutionTime:desc";
      if (gridState?.recordsPerPage) params.limit = gridState.recordsPerPage;
      const DateFilter = dateRange === null ? reportDateRange : dateRange;

      if (DateFilter && Array.isArray(DateFilter)) {
        const [startDate, endDate] = DateFilter;

        const gte = { d: moment(startDate).toISOString() };
        const lt = { d: moment(endDate).add(1, "day").toISOString() };

        params.filters.push(["lastExecutionTime", "gte", gte.d]);
        params.filters.push(["lastExecutionTime", "lt", lt.d]);
      }
      if (!params.filters?.length) return;
      setFilterValue(params.filters);
      dispatch(hideLoader());
    } catch (err) {
      console.log("error:::::::::", err);
      dispatch(hideLoader());
    }
  };

  // ** Sort dropdown data
  function compareCategories(a, b) {
    // Move "All" category to the beginning
    if (a.value === "All") return -1;
    if (b.value === "All") return 1;
    // Sort the rest in ascending order based on label
    return a.label.localeCompare(b.label);
  }

  //** handle reset function
  const handleReset = () => {
    window.location.reload();
  };

  //** handle date range
  const fnStartDate = () => {
    let dateFrom = new Date();
    let dateTo = new Date();
    dateFrom.setDate(dateFrom.getDate() - CONSTANTS.BOREPORTDEFAULTDATERANGE);
    const dateStart = dateFrom
      .toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");
    dateTo.setDate(dateTo.getDate());
    const dateEnd = dateTo
      .toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");
    setStartDate(new Date(dateStart));
    setEndDate(new Date(dateEnd));
    setDateRange([dateFrom, dateTo]);
    return [dateFrom, dateTo];
  };

  useEffect(() => {
    (async () => {
      const reportDateRange = fnStartDate();
      // await fetchAnalystQueryReport(reportDateRange);
      hideLoader();
    })();
  }, []);

  //** Set breadcrumb
  useEffect(() => {
    dispatch(setBreadCrumb(breadCrumbLinks));
    return () => {
      dispatch(setBreadCrumb());
    };
  }, [dispatch]);

  const fetchAutomationRuleChangeReportData = (params = {}) => {
    let defaultFilter = [];
    let { filters = [] } = params;
    params.filters = [...filters, ...defaultFilter];
    if (!params.sort) {
      params.sort = "ruleName:asc";
    }
    if (!gridState.recordVisited) {
      if (!params.skip) {
        params.skip = 0;
      }
      // dispatch(setMaintainedState(params.skip, params.filters, params.sort));
    }
    if (gridState.recordVisited) {
      params = {
        filters: gridState.filtersParams,
        sort: gridState.sortRecords,
        skip: gridState.skipRecords,
        limit: gridState.recordsPerPage,
      };
      //dispatch(setMaintainedState(params.skip, params.filters, params.sort));
    }
    if (params.filters?.length === 0) {
      params.filters = [...filters, ...defaultFilter];
      clearLocalGrid();
      return fetchAutomationRuleChangeReport(params);
    }
    clearLocalGrid();
    return fetchAutomationRuleChangeReport(params);
  };

  const clearLocalGrid = () => {
    const arr = []; // Array to hold the keys
    // Iterate over localStorage and insert the keys that meet the condition into arr
    for (let i = 0; i < localStorage.length; i++) {
      if (localStorage.key(i).substring(0, 8) == "gridgrid") {
        arr.push(localStorage.key(i));
      }
    }
    for (let i of arr) {
      localStorage.removeItem(i);
    }
  };

  const fetchAutomationExecutionDetailedReportData = (params = {}) => {
    let defaultFilter = [["parent_id", "eq", selectedRuleId]];
    let { filters = [] } = params;
    params.filters = [...filters, ...defaultFilter];
    if (!params.sort) {
      params.sort = "ruleName:asc";
    }
    if (!gridState.recordVisited) {
      if (!params.skip) {
        params.skip = 0;
      }
      // dispatch(setMaintainedState(params.skip, params.filters, params.sort));
    }
    if (gridState.recordVisited) {
      params = {
        filters: gridState.filtersParams,
        sort: gridState.sortRecords,
        skip: gridState.skipRecords,
        limit: gridState.recordsPerPage,
      };
      //dispatch(setMaintainedState(params.skip, params.filters, params.sort));
    }
    if (params.filters?.length === 0) {
      params.filters = [...filters, ...defaultFilter];
      clearLocalGrid();
      return fetchAutomationExecutionDetailedReport(params);
    }
    clearLocalGrid();
    return fetchAutomationExecutionDetailedReport(params);
  };

  const exportAutomationRuleChangeData = async (flag, ids) => {
    if (flag) {
      const payload = {
        reqIds: JSON.stringify(ids),
      };
      if (gridState.filtersParams) payload.filters = gridState.filtersParams;
      dispatch(showLoader());
      await exportAutomationRuleChangeReportData(payload)
        .then((res) => {
          const link = document.createElement("a");
          link.href = downloadAutomationRuleChangeFileUrl(`${res.data.data}`);
          link.download = `${res.data.data}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          dispatch(hideLoader());
        })
        .catch((err) => {
          dispatch(actionError("Something Went Wrong...!"));
          console.error("Error: ", err);
        });
    }
  };

  const exportExecutionDetailedData = async (flag, ids) => {
    if (flag) {
      const payload = {
        reqIds: JSON.stringify(ids),
      };
      if (gridState.filtersParams) payload.filters = gridState.filtersParams;
      dispatch(showLoader());
      await exportExecutionDetailedReportData(payload)
        .then((res) => {
          const link = document.createElement("a");
          link.href = downloadAutomationExecutionDetailedFileUrl(`${res.data.data}`);
          link.download = `${res.data.data}`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          dispatch(hideLoader());
        })
        .catch((err) => {
          dispatch(actionError("Something Went Wrong...!"));
          console.error("Error: ", err);
        });
    }
  };

  return (
    <div className="px-3 grid-bg-white data-report-container">
      <div className="col-md-12 rounded-bottom nopadding p-0 bg-white ">
        <div className="row">
          <div className="col-md-2 mt-3 grid-container-report-filters pr-0 responsive-report-filters">
            <DateRangePickerComponent
              placeholder="Date Range"
              cssClass="e-outline analyst-query-datepicker"
              openOnFocus={true}
              showClearButton={false}
              format="dd MMM yyyy"
              separator="to"
              floatLabelType="Auto"
              startDate={startDate}
              endDate={endDate}
              onChange={(e) => {
                if (e.value) setDateRange(e.value);
              }}
              autoComplete="off"
              max={new Date()}
              strictMode={true}
              presets={presets}
            />
          </div>
          <div className="col-md-3 mt-3">
            <button type="button" onClick={() => fetchAnalystQueryReport(null, {}, true)} className="search-btn px-4 btn btn-primary mr-2">
              Search
            </button>
            <button type="reset" onClick={handleReset} className="btn btn-outline-dark px-4 ml-2 ">
              Reset
            </button>
          </div>
        </div>
      </div>
      <div className="mt-3 automation-rule-change-report-grid">
        <CommonDataGrid
          gridTitle="Automation Rule Change Report Records"
          fetch={fetchAutomationRuleChangeReportData}
          showLoader={showLoaderGrid}
          hideLoader={hideLoaderGrid}
          columnNames={automationCol}
          columnFields={columnFields}
          formatRowData={formatRowData}
          exportRight={true}
          isExport={exportAutomationRuleChangeData}
          deleteMany={null}
          filterValue={filterValue}
        />
        <NewPagination
          rowData={rowData}
          allowPagination={true}
          showLoader={showLoaderGrid}
          hideLoader={hideLoaderGrid}
          totalCount={totalCount}
          setRowData={setRowData}
          fetch={fetchAutomationRuleChangeReportData}
          defaultPageView={CONSTANTS.DEFAULTANALYSTREPORTPAGEVIEW}
        />
      </div>
      {showSidePanel && (
        <CustomSidebar sidebarRef={sidebarRef} isOpen={showSidePanel} sidebarId="execution-detailed-report-sidebar">
          <div className="execution-detailed-report-sidebar">
            <div className="execution-detailed-report-sidebar-header">
              <span className="sidebar-header-name">Execution Detailed Report</span>
              <span className="sidebar-cross-icon" onClick={() => handelSideBar()}>
                <CloseIcon />
              </span>
            </div>
            <div className="execution-detailed-report-grid">
              <CommonDataGrid
                gridTitle="Execution Detailed Report Records"
                fetch={fetchAutomationExecutionDetailedReportData}
                showLoader={showLoaderGrid}
                hideLoader={hideLoaderGrid}
                columnNames={executionCol}
                columnFields={modalColumnFields}
                formatRowData={formatExecutionRowData}
                exportRight={true}
                isExport={exportExecutionDetailedData}
                deleteMany={null}
              />
              <NewPagination
                rowData={rowData}
                allowPagination={true}
                showLoader={showLoaderGrid}
                hideLoader={hideLoaderGrid}
                totalCount={totalCount}
                setRowData={setRowData}
                fetch={fetchAutomationExecutionDetailedReportData}
                defaultPageView={CONSTANTS.DEFAULTANALYSTREPORTPAGEVIEW}
              />
            </div>
            <div className="execution-detailed-report-grid-footer">
              <button className="btn btn-outline-dark" onClick={() => handelSideBar()}>
                Cancel
              </button>
            </div>
          </div>
        </CustomSidebar>
      )}
    </div>
  );
};

export default AutomationRuleChangeReport;
